import { useCallback } from 'react';

import { useSelectionChannel } from '../stores/useSelectionChannel';

/**
 * useSelectionItems 반환 타입
 */
interface UseSelectionItemsResult<T> {
  /**
   * 선택 여부
   */
  hasSelection: boolean;

  /**
   * 선택된 항목 ID 배열
   */
  ids: string[];

  /**
   * 다중 선택 여부
   */
  isMultiSelected: boolean;

  /**
   * 단일 선택 여부
   */
  isSingleSelected: boolean;

  /**
   * 선택된 항목 데이터 배열
   * - 선택이 없거나 items가 없으면 빈 배열
   */
  items: T[];
}

/**
 * SelectionChannel에서 선택된 항목 데이터를 조회하는 훅
 *
 * @description
 * - List에서 usePanelSelectionSync로 전달한 selectedItems를 Panel에서 조회
 * - 추가 API 호출 없이 다중 선택 시 비교 뷰 등에서 데이터 사용 가능
 * - Zustand selector를 사용하여 특정 scopeKey의 selection만 구독
 *   (useSyncExternalStore 대신 사용하여 무한루프 방지)
 *
 * @param scopeKey - Scope 키 (Panel Scope Registry 식별자)
 * @returns 선택된 항목 데이터 및 상태
 *
 * @example
 * ```tsx
 * // Panel 컴포넌트에서 사용
 * function SubnetDetailMultiContent({ subnetIds }: Props) {
 *   const { items } = useSelectionItems<Subnet>('vpc-subnet-list');
 *
 *   // items가 있으면 바로 사용, 없으면 API 호출
 *   const subnets = items.length > 0 ? items : fetchedSubnets;
 * }
 * ```
 */
export default function useSelectionItems<T = unknown>(
  scopeKey: string,
): UseSelectionItemsResult<T> {
  // Zustand selector로 특정 scopeKey의 selection만 구독
  // useSyncExternalStore + subscribeSelection 조합은 무한루프를 유발하므로 사용하지 않음
  const selection = useSelectionChannel(
    useCallback(state => state.selections.get(scopeKey) ?? null, [scopeKey]),
  );

  const ids = selection?.ids ?? [];
  const items = (selection?.items as T[]) ?? [];
  const hasSelection = ids.length > 0;
  const isSingleSelected = ids.length === 1;
  const isMultiSelected = ids.length > 1;

  return {
    items,
    ids,
    hasSelection,
    isSingleSelected,
    isMultiSelected,
  };
}
